(() => {
  const getScrollHero = (root) => {
    if (!root || typeof root.querySelector !== 'function') return null;
    return root.matches?.('[data-teka-scroll-hero]')
      ? root
      : root.querySelector('[data-teka-scroll-hero]');
  };

  const getHomepageScrollContainer = () => {
    const pageWrapper = document.querySelector('.page-wrapper');
    if (!pageWrapper) return window;

    const overflowY = window.getComputedStyle(pageWrapper).overflowY;
    return ['auto', 'scroll', 'overlay'].includes(overflowY) ? pageWrapper : window;
  };

  const initScrollHero = (root) => {
    const hero = getScrollHero(root);
    if (!hero || hero.__tekaScrollHeroCleanup) return;

    const track = hero.closest('[data-teka-hero-track]');
    const media = hero.querySelector('[data-teka-hero-media]');
    const video = hero.querySelector('[data-teka-hero-video]');
    const copy = hero.querySelector('[data-teka-hero-copy]');
    if (!track || !media || !video || !copy) return;

    const motionEnabled = hero.dataset.enableScrollMotion !== 'false';
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const canAnimate = motionEnabled && !reducedMotionQuery.matches;
    const scrollContainer = getHomepageScrollContainer();
    const headerGroup = document.querySelector('#header-group');
    const headerComponent = document.querySelector('#header-component');
    let animationFrame = 0;
    let smoothedProgress = 0;
    let lastFrameTime = 0;
    let pendingVideoTime = null;
    let videoSeekInFlight = false;
    let observer;
    let resizeObserver;
    let headerStateObserver;
    let duration = 0;
    let lastSeekStartedAt = 0;
    const minSeekInterval = 1000 / 30;
    const seekThreshold = 1 / 30;
    const darkCopyEndTime = 3.5;
    const darkCopyTime = 7.5;
    let geometry = {
      stickyTop: 0,
      scrollDistance: 1,
    };

    const getScrollTop = () => scrollContainer === window ? window.scrollY : scrollContainer.scrollTop;

    const getHeaderOffset = () => {
      if (headerGroup) {
        const height = [...headerGroup.children].reduce((total, child) => {
          return total + (child instanceof HTMLElement ? child.getBoundingClientRect().height : 0);
        }, 0);
        if (height > 0) return height;
      }

      return headerComponent?.getBoundingClientRect().height || 0;
    };

    const syncHeaderOffset = () => {
      const height = getHeaderOffset();
      document.body.style.setProperty('--teka-hero-header-offset', `${height}px`);
    };

    const refreshGeometry = () => {
      const scrollTop = getScrollTop();
      const heroHeight = hero.getBoundingClientRect().height;
      const stickyTop = Number.parseFloat(window.getComputedStyle(hero).top) || 0;

      geometry = {
        stickyTop,
        scrollDistance: Math.max(track.offsetHeight - heroHeight, 1),
      };
    };

    const handleSeeked = () => {
      videoSeekInFlight = false;
      if (pendingVideoTime !== null) {
        scheduleUpdate();
      }
    };

    const requestVideoSeek = (time) => {
      if (duration <= 0 || video.readyState < 1) return;

      pendingVideoTime = time;
      if (videoSeekInFlight) return;

      if (performance.now() - lastSeekStartedAt < minSeekInterval) {
        scheduleUpdate();
        return;
      }

      const nextTime = pendingVideoTime;
      pendingVideoTime = null;
      if (Math.abs(nextTime - video.currentTime) < seekThreshold) return;

      try {
        videoSeekInFlight = true;
        lastSeekStartedAt = performance.now();
        video.currentTime = nextTime;
      } catch (error) {
        videoSeekInFlight = false;
        if (error?.name !== 'AbortError') console.warn('[teka-hero] Video seek failed:', error);
      }
    };

    const update = (timestamp) => {
      animationFrame = 0;
      if (!canAnimate) return;

      const trackTop = track.getBoundingClientRect().top;
      const targetProgress = Math.min(1, Math.max(0, (geometry.stickyTop - trackTop) / geometry.scrollDistance));
      const elapsed = lastFrameTime ? Math.min(timestamp - lastFrameTime, 100) : 16;
      const smoothing = 1 - Math.exp(-elapsed / 120);
      smoothedProgress += (targetProgress - smoothedProgress) * smoothing;
      lastFrameTime = timestamp;
      duration = Number.isFinite(video.duration) ? video.duration : duration;
      const currentTime = smoothedProgress * duration;
      copy.classList.toggle('is-dark-copy', duration === 0 || currentTime <= darkCopyEndTime || currentTime >= darkCopyTime);

      if (duration > 0 && video.readyState >= 1) {
        requestVideoSeek(smoothedProgress * duration);
      }

      if (Math.abs(targetProgress - smoothedProgress) > 0.001 || videoSeekInFlight || pendingVideoTime !== null) {
        animationFrame = window.requestAnimationFrame(update);
      }
    };

    const scheduleUpdate = () => {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(update);
    };

    const handleResize = () => {
      syncHeaderOffset();
      refreshGeometry();
      scheduleUpdate();
    };

    const prepareVideo = () => {
      if (video.readyState === 0) video.load();
      if (canAnimate) scheduleUpdate();
    };

    const handleVideoReady = () => {
      duration = Number.isFinite(video.duration) ? video.duration : duration;
      if (!reducedMotionQuery.matches) media.classList.add('is-video-ready');
      if (canAnimate) scheduleUpdate();
    };

    const handleMetadata = () => {
      duration = Number.isFinite(video.duration) ? video.duration : duration;
      scheduleUpdate();
    };

    video.addEventListener('loadedmetadata', handleMetadata);
    video.addEventListener('durationchange', handleMetadata);
    video.addEventListener('canplay', handleVideoReady);
    video.addEventListener('seeked', handleSeeked);
    video.pause();
    syncHeaderOffset();
    refreshGeometry();
    prepareVideo();

    if (canAnimate) {
      media.classList.add('is-motion-active');
      window.addEventListener('scroll', scheduleUpdate, { passive: true });
      if (scrollContainer !== window) {
        scrollContainer.addEventListener('scroll', scheduleUpdate, { passive: true });
      }
      window.addEventListener('resize', handleResize, { passive: true });
      scheduleUpdate();
    } else {
      video.currentTime = 0;
    }

    if (window.IntersectionObserver) {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          prepareVideo();
          observer.disconnect();
        }
      }, { rootMargin: '300px 0px' });
      observer.observe(hero);
    } else {
      prepareVideo();
    }

    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        syncHeaderOffset();
        refreshGeometry();
        scheduleUpdate();
      });
      resizeObserver.observe(track);
      resizeObserver.observe(hero);
      if (headerComponent) resizeObserver.observe(headerComponent);
      if (headerGroup) {
        [...headerGroup.children].forEach((child) => resizeObserver.observe(child));
      }
    }

    if (window.MutationObserver && headerComponent) {
      headerStateObserver = new MutationObserver(() => {
        syncHeaderOffset();
        refreshGeometry();
        scheduleUpdate();
      });
      headerStateObserver.observe(headerComponent, {
        attributes: true,
        attributeFilter: ['data-sticky-state', 'data-scroll-direction'],
      });
    }

    hero.__tekaScrollHeroCleanup = () => {
      observer?.disconnect();
      resizeObserver?.disconnect();
      headerStateObserver?.disconnect();
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('scroll', scheduleUpdate);
      if (scrollContainer !== window) {
        scrollContainer.removeEventListener('scroll', scheduleUpdate);
      }
      window.removeEventListener('resize', handleResize);
      video.removeEventListener('loadedmetadata', handleMetadata);
      video.removeEventListener('durationchange', handleMetadata);
      video.removeEventListener('canplay', handleVideoReady);
      video.removeEventListener('seeked', handleSeeked);
      video.pause();
      delete hero.__tekaScrollHeroCleanup;
    };
  };

  const cleanupScrollHero = (root) => {
    const hero = getScrollHero(root);
    hero?.__tekaScrollHeroCleanup?.();
  };

  const initHomepage = (root = document) => {
    initScrollHero(root);
    const menu = root.querySelector('.teka-site-header__menu');

    if (menu && !menu.dataset.tekaInitialized) {
      menu.dataset.tekaInitialized = 'true';
      let closeTimer;

      const closeMenu = () => {
        menu.dataset.tekaClosing = 'true';
        menu.setAttribute('aria-expanded', 'false');
        menu.setAttribute('aria-label', 'Open menu');
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          delete menu.dataset.tekaClosing;
        }, 280);
      };

      menu.addEventListener('click', () => {
        const expanded = menu.getAttribute('aria-expanded') === 'true';
        window.clearTimeout(closeTimer);

        if (expanded) {
          closeMenu();
          return;
        }

        delete menu.dataset.tekaClosing;
        menu.setAttribute('aria-expanded', 'true');
        menu.setAttribute('aria-label', 'Close menu');
      });

      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeMenu();
          menu.focus();
        }
      });
    }

    const navigation = root.querySelector('.teka-site-header__nav');
    if (navigation && !navigation.dataset.tekaOverflowInitialized) {
      const mainMenu = navigation.querySelector(':scope > .teka-menu-links--level-0');
      const overflow = navigation.querySelector('[data-teka-nav-overflow]');
      const overflowLinks = navigation.querySelector('[data-teka-nav-overflow-links]');
      const overflowToggle = overflow?.querySelector('.teka-nav-overflow__toggle');

      if (mainMenu && overflow && overflowLinks && overflowToggle) {
        navigation.dataset.tekaOverflowInitialized = 'true';
        const menuItems = [...mainMenu.children];
        let resizeFrame = 0;

        const closeOverflow = () => {
          overflow.classList.remove('is-open');
          overflowToggle.setAttribute('aria-expanded', 'false');
        };

        const fitMenu = () => {
          menuItems.forEach((item) => mainMenu.appendChild(item));
          overflow.hidden = true;
          closeOverflow();

          if (!window.matchMedia('(min-width: 750px)').matches || !menuItems.length) return;

          const navigationStyle = window.getComputedStyle(navigation);
          const menuStyle = window.getComputedStyle(mainMenu);
          const navigationPadding = (Number.parseFloat(navigationStyle.paddingLeft) || 0)
            + (Number.parseFloat(navigationStyle.paddingRight) || 0);
          const navigationWidth = navigation.clientWidth - navigationPadding;
          const menuGap = Number.parseFloat(menuStyle.columnGap) || 0;
          const itemWidths = menuItems.map((item) => item.getBoundingClientRect().width);
          const fullMenuWidth = itemWidths.reduce((total, width) => total + width, 0)
            + menuGap * Math.max(0, itemWidths.length - 1);

          if (fullMenuWidth <= navigationWidth + 1) return;

          overflow.hidden = false;
          const navigationGap = Number.parseFloat(window.getComputedStyle(navigation).columnGap) || 0;
          const toggleWidth = overflow.getBoundingClientRect().width;
          const menuWidthAvailable = navigationWidth - toggleWidth - navigationGap;
          let visibleCount = 0;
          let visibleWidth = 0;

          for (const itemWidth of itemWidths) {
            const nextWidth = visibleWidth + (visibleCount ? menuGap : 0) + itemWidth;
            if (nextWidth > menuWidthAvailable + 1) break;
            visibleWidth = nextWidth;
            visibleCount += 1;
          }

          while (mainMenu.children.length > visibleCount) {
            overflowLinks.prepend(mainMenu.lastElementChild);
          }

          overflow.hidden = overflowLinks.children.length === 0;
        };

        const scheduleFit = () => {
          window.cancelAnimationFrame(resizeFrame);
          resizeFrame = window.requestAnimationFrame(fitMenu);
        };
        const closeOnOutsidePointer = (event) => {
          if (!overflow.contains(event.target)) closeOverflow();
        };

        overflowToggle.addEventListener('click', () => {
          const isOpen = overflow.classList.toggle('is-open');
          overflowToggle.setAttribute('aria-expanded', String(isOpen));
        });
        document.addEventListener('pointerdown', closeOnOutsidePointer);
        navigation.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && overflow.classList.contains('is-open')) {
            closeOverflow();
            overflowToggle.focus();
          }
        });

        let navResizeObserver;
        if (window.ResizeObserver) {
          navResizeObserver = new ResizeObserver(scheduleFit);
          navResizeObserver.observe(navigation);
        } else {
          window.addEventListener('resize', scheduleFit);
        }
        navigation.__tekaOverflowCleanup = () => {
          navResizeObserver?.disconnect();
          window.removeEventListener('resize', scheduleFit);
          document.removeEventListener('pointerdown', closeOnOutsidePointer);
          window.cancelAnimationFrame(resizeFrame);
          delete navigation.__tekaOverflowCleanup;
        };
        scheduleFit();
        document.fonts?.ready.then(scheduleFit);
      }
    }

    if (navigation && !navigation.dataset.tekaHoverInitialized) {
      navigation.dataset.tekaHoverInitialized = 'true';
      navigation.addEventListener('pointerleave', () => {
        if (window.matchMedia('(min-width: 750px)').matches) {
          navigation.querySelectorAll('.teka-menu-root-trigger[aria-expanded="true"]').forEach((trigger) => {
            trigger.setAttribute('aria-expanded', 'false');
          });
        }
      });

      const activateCategory = (button) => {
        const item = button.closest('.teka-menu-item--has-children');
        if (!item) return;
        const categories = [...item.querySelectorAll('[data-teka-mega-category]')];
        const panels = [...item.querySelectorAll('[data-teka-mega-panel]')];
        const index = categories.indexOf(button);
        if (index < 0) return;
        categories.forEach((category, categoryIndex) => {
          const active = categoryIndex === index;
          category.classList.toggle('is-active', active);
          category.setAttribute('aria-selected', String(active));
          category.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel, panelIndex) => {
          const active = panelIndex === index;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
      };

      navigation.addEventListener('pointerover', (event) => {
        if (!window.matchMedia('(min-width: 750px)').matches) return;
        const hoveredTopLevelItem = event.target.closest('.teka-menu-links > .teka-menu-item');
        navigation.querySelectorAll('.teka-menu-item--has-children > .teka-menu-root-trigger[aria-expanded="true"]').forEach((trigger) => {
          const openItem = trigger.closest('.teka-menu-item');
          if (openItem !== hoveredTopLevelItem) {
            trigger.setAttribute('aria-expanded', 'false');
            if (openItem.contains(document.activeElement)) document.activeElement.blur();
          }
        });
        const rootTrigger = event.target.closest('.teka-menu-root-trigger');
        if (rootTrigger) rootTrigger.setAttribute('aria-expanded', 'true');
        const category = event.target.closest('[data-teka-mega-category]');
        if (category) activateCategory(category);
      });
      navigation.addEventListener('focusin', (event) => {
        const category = event.target.closest('[data-teka-mega-category]');
        if (category) activateCategory(category);
      });

      navigation.addEventListener('click', (event) => {
        const rootTrigger = event.target.closest('.teka-menu-root-trigger');
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        if (rootTrigger && mobile) {
          const item = rootTrigger.closest('.teka-menu-item--has-children');
          const opening = !item.classList.contains('is-mobile-open');
          navigation.querySelectorAll('.teka-menu-item--has-children.is-mobile-open').forEach((openItem) => {
            openItem.classList.remove('is-mobile-open');
            openItem.querySelector('.teka-menu-root-trigger')?.setAttribute('aria-expanded', 'false');
          });
          if (opening) {
            item.classList.add('is-mobile-open');
            rootTrigger.setAttribute('aria-expanded', 'true');
            item.dataset.tekaMobilePage = '0';
            const track = item.querySelector('[data-teka-mobile-track]');
            if (track) {
              track.style.transform = 'translateX(0)';
              track.querySelectorAll('[data-teka-mobile-page]').forEach((panel, index) => {
                panel.setAttribute('aria-hidden', String(index !== 0));
                panel.inert = index !== 0;
              });
            }
          }
          return;
        }

        const next = event.target.closest('[data-teka-mobile-next]');
        if (next && mobile) {
          const item = next.closest('.teka-menu-item--has-children');
          const track = item?.querySelector('[data-teka-mobile-track]');
          const page = Number(next.dataset.tekaMobileNext);
          if (track && Number.isFinite(page)) {
            track.style.transform = `translateX(-${page * 100}%)`;
            item.dataset.tekaMobilePage = String(page);
            item.querySelectorAll('[data-teka-mobile-page]').forEach((panel, panelIndex) => {
              panel.setAttribute('aria-hidden', String(panelIndex !== page));
              panel.inert = panelIndex !== page;
            });
            item.querySelectorAll('[data-teka-mobile-page]')[page]?.querySelector('.teka-mega-menu__back')?.focus({ preventScroll: true });
          }
          return;
        }

        const back = event.target.closest('[data-teka-mobile-back]');
        if (back && mobile) {
          const item = back.closest('.teka-menu-item--has-children');
          const track = item?.querySelector('[data-teka-mobile-track]');
          const pageIndex = back.closest('[data-page-index]')?.dataset.pageIndex;
          if (track) track.style.transform = 'translateX(0)';
          item?.querySelectorAll('[data-teka-mobile-page]').forEach((panel, panelIndex) => {
            panel.setAttribute('aria-hidden', String(panelIndex !== 0));
            panel.inert = panelIndex !== 0;
          });
          item?.querySelector(`[data-teka-mobile-next="${pageIndex}"]`)?.focus({ preventScroll: true });
          return;
        }

        const close = event.target.closest('[data-teka-mobile-close]');
        if (close && mobile) {
          const item = close.closest('.teka-menu-item--has-children');
          item?.classList.remove('is-mobile-open');
          item?.querySelector('.teka-menu-root-trigger')?.setAttribute('aria-expanded', 'false');
          item?.querySelector('.teka-menu-root-trigger')?.focus({ preventScroll: true });
        }
      });

      navigation.querySelectorAll('[data-teka-mobile-track]').forEach((track) => {
        let touchStartX = null;
        track.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch') touchStartX = event.clientX;
        }, { passive: true });
        track.addEventListener('pointerup', (event) => {
          if (event.pointerType !== 'touch' || touchStartX === null) return;
          const deltaX = event.clientX - touchStartX;
          touchStartX = null;
          if (Math.abs(deltaX) < 55) return;
          const item = track.closest('.teka-menu-item--has-children');
          const currentPage = Number(item?.dataset.tekaMobilePage || 0);
          const nextPage = deltaX < 0
            ? Math.min(currentPage + 1, track.querySelectorAll('[data-teka-mobile-page]').length - 1)
            : Math.max(currentPage - 1, 0);
          if (nextPage === currentPage) return;
          track.style.transform = `translateX(-${nextPage * 100}%)`;
          item.dataset.tekaMobilePage = String(nextPage);
          track.querySelectorAll('[data-teka-mobile-page]').forEach((panel, panelIndex) => {
            panel.setAttribute('aria-hidden', String(panelIndex !== nextPage));
            panel.inert = panelIndex !== nextPage;
          });
        }, { passive: true });
        track.addEventListener('pointercancel', () => { touchStartX = null; }, { passive: true });
      });

      navigation.addEventListener('keydown', (event) => {
        const category = event.target.closest('[data-teka-mega-category]');
        if (category && ['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(event.key)) {
          const item = category.closest('.teka-menu-item--has-children');
          const categories = [...(item?.querySelectorAll('[data-teka-mega-category]') || [])];
          const currentIndex = categories.indexOf(category);
          const direction = ['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1;
          const nextCategory = categories[(currentIndex + direction + categories.length) % categories.length];
          if (nextCategory) {
            event.preventDefault();
            activateCategory(nextCategory);
            nextCategory.focus();
          }
          return;
        }
        if (event.key === 'Escape') {
          const item = event.target.closest('.teka-menu-item--has-children');
          if (item) {
            item.classList.remove('is-mobile-open');
            item.querySelector('.teka-menu-root-trigger')?.setAttribute('aria-expanded', 'false');
            item.querySelector('.teka-menu-root-trigger')?.focus();
          }
        }
      });

      navigation.querySelectorAll('[data-teka-mobile-track]').forEach((track) => {
        track.querySelectorAll('[data-teka-mobile-page]').forEach((panel, index) => {
          panel.setAttribute('aria-hidden', String(index !== 0));
          panel.inert = index !== 0;
        });
      });
      navigation.querySelectorAll('.teka-menu-item--has-children').forEach((item) => {
        item.querySelectorAll('[data-teka-mega-category]').forEach((category, index) => {
          category.tabIndex = index === 0 ? 0 : -1;
        });
      });
    }

    const trustBar = root.querySelector('.teka-trust-bar');
    if (trustBar && !trustBar.dataset.tekaInitialized) {
      const items = [...trustBar.querySelectorAll('li')];
      trustBar.dataset.tekaInitialized = 'true';

      if (items.length > 1) {
        let activeIndex = 0;
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        items.forEach((item, index) => {
          item.classList.toggle('is-active', index === activeIndex);
          item.setAttribute('aria-hidden', isMobile && index !== activeIndex ? 'true' : 'false');
        });

        if (isMobile && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          window.setInterval(() => {
            const currentItem = items[activeIndex];
            currentItem.classList.add('is-leaving');
            currentItem.setAttribute('aria-hidden', 'true');
            activeIndex = (activeIndex + 1) % items.length;
            items[activeIndex].classList.add('is-active');
            items[activeIndex].setAttribute('aria-hidden', 'false');

            window.setTimeout(() => {
              currentItem.classList.remove('is-active', 'is-leaving');
            }, 350);
          }, 3000);
        }
      }
    }

    const sections = root.matches?.('.teka-home-section')
      ? [root]
      : [...root.querySelectorAll('.teka-home-section')];

    const articleSection = root.matches?.('.teka-articles-section')
      ? root
      : root.querySelector('.teka-articles-section');
    if (articleSection && !articleSection.dataset.tekaInitialized) {
      const slideshow = articleSection.querySelector('slideshow-component');
      const previous = articleSection.querySelector('[data-teka-articles-previous]');
      const next = articleSection.querySelector('[data-teka-articles-next]');

      if (slideshow && previous && next) {
        const updateArticleControls = () => {
          previous.disabled = slideshow.atStart;
          next.disabled = slideshow.atEnd;
        };

        articleSection.dataset.tekaInitialized = 'true';
        previous.addEventListener('click', () => slideshow.previous());
        next.addEventListener('click', () => slideshow.next());
        slideshow.addEventListener('slideshow:select', updateArticleControls);
        updateArticleControls();
      }
    }

    if (!sections.length) return;

    sections.forEach((section) => {
      const brandCarousel = section.querySelector('.teka-brand-categories');
      if (brandCarousel && !brandCarousel.dataset.tekaInitialized) {
        const grid = brandCarousel.querySelector('[data-teka-brand-grid]');
        const viewport = brandCarousel.querySelector('[data-teka-brand-viewport]');
        const previous = brandCarousel.querySelector('[data-teka-brand-previous]');
        const next = brandCarousel.querySelector('[data-teka-brand-next]');

        if (grid && viewport && previous && next) {
          const updateBrandControls = () => {
            const maxScroll = grid.scrollWidth - grid.clientWidth;
            const isScrollable = maxScroll > 1;
            const atStart = grid.scrollLeft <= 1;
            const atEnd = grid.scrollLeft >= maxScroll - 1;
            const position = atStart ? 'start' : atEnd ? 'end' : 'middle';

            viewport.dataset.scrollable = String(isScrollable);
            viewport.dataset.position = position;
            grid.dataset.scrollable = String(isScrollable);
            grid.dataset.position = position;
            previous.disabled = !isScrollable || atStart;
            next.disabled = !isScrollable || atEnd;
          };

          const scrollBrands = (direction) => {
            grid.scrollBy({ left: direction * grid.clientWidth * 0.8, behavior: 'smooth' });
          };

          brandCarousel.dataset.tekaInitialized = 'true';
          previous.addEventListener('click', () => scrollBrands(-1));
          next.addEventListener('click', () => scrollBrands(1));
          grid.addEventListener('scroll', updateBrandControls, { passive: true });
          updateBrandControls();
        }
      }

      section.querySelectorAll('.rv').forEach((headline) => {
        if (headline.dataset.tekaInitialized) return;

        headline.dataset.tekaInitialized = 'true';
        const tokens = headline.innerHTML
          .replace(/<br\s*\/?\s*>/gi, ' __BR__ ')
          .replace(/<[^>]+>/g, '')
          .trim()
          .split(/\s+/);
        headline.innerHTML = tokens
          .map((word) => word === '__BR__' ? '<br>' : `<span class="w"><span>${word}</span></span>`)
          .join(' ');
        [...headline.querySelectorAll('.w > span')].forEach((word, index) => {
          word.style.transitionDelay = `${index * 55}ms`;
        });
      });

      section.querySelectorAll('section').forEach((sectionElement) => {
        sectionElement.querySelectorAll('.fade, .teka-project-card').forEach((element, index) => {
          element.style.transitionDelay = `${120 + index * 70}ms`;
        });
      });

      const reveal = section.querySelectorAll('.rv,.fade,.teka-project-card');
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !window.IntersectionObserver) {
        reveal.forEach((item) => item.classList.add('in'));
        return;
      }

      const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          observer.unobserve(entry.target);
        }
      }), { threshold: 0.18 });
      reveal.forEach((item) => observer.observe(item));
    });
  };

  initHomepage();

  document.addEventListener('shopify:cart:lines-update', (event) => {
    event.promise?.then(({ cart, detail }) => {
      const itemCount = cart?.totalQuantity ?? detail?.itemCount;
      if (itemCount === undefined) return;

      document.querySelectorAll('[data-teka-cart-count]').forEach((count) => {
        count.textContent = itemCount < 100 ? String(itemCount) : '';
      });
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[teka-header] Cart count update failed:', error);
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) initHomepage(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof HTMLElement) {
      cleanupScrollHero(event.target);
      const navigation = event.target.matches('.teka-site-header__nav')
        ? event.target
        : event.target.querySelector('.teka-site-header__nav');
      navigation?.__tekaOverflowCleanup?.();
    }
  });
})();
